import type { FastifyInstance } from 'fastify'
import type { CollabController } from './collab.controller.js'
import { authMiddleware } from '../../middleware/auth.middleware.js'

export function collabRoutes(fastify: FastifyInstance, controller: CollabController) {

  // ===== CRDT REAL-TIME COLLABORATION =====

  // CRDT sync endpoint (binary, high security)
  // Note: rawBody is disabled because the global application/octet-stream parser
  // in server.ts already handles binary body parsing correctly
  fastify.post<{ Params: { planId: string }; Body: Buffer }>(
    '/plans/:planId/sync',
    {
      preHandler: [authMiddleware],
      bodyLimit: 10485760, // 10MB limit for Y.Doc updates
      config: {
        rawBody: false, // Skip fastify-raw-body plugin to prevent stream conflicts
        rateLimit: {
          max: 60,
          timeWindow: '1 minute'
        }
      }
    },
    async (request, reply) => {
      return controller.sync(request, reply)
    }
  )

  // Get presence (who's online, cursors)
  fastify.get<{ Params: { planId: string } }>(
    '/plans/:planId/presence',
    {
      config: {
        rateLimit: {
          max: 120,
          timeWindow: '1 minute'
        }
      }
    },
    async (request, reply) => {
      return controller.getPresence(request, reply)
    }
  )

  // Update presence (cursor position)
  fastify.post<{ Params: { planId: string }; Body: { cursor?: any; deviceId?: string; timestamp?: number } }>(
    '/plans/:planId/presence',
    {
      preHandler: [authMiddleware],
      config: {
        rateLimit: {
          max: 120,
          timeWindow: '1 minute'
        }
      }
    },
    async (request, reply) => {
      return controller.updatePresence(request, reply)
    }
  )

  // ===== SHARING & COMMENTS =====

  // Create share (auth required)
  fastify.post<{ Params: { planId: string }; Body: { privacy: 'public' | 'private'; password?: string; expiresAt?: string | number; allowComments?: boolean } }>(
    '/plans/:planId/share',
    { preHandler: [authMiddleware] },
    async (request, reply) => {
      const { planId } = request.params
      const share = await controller.createShare(request, reply, planId, request.body as any)
      if (share) return reply.send(share)
    }
  )

  // List shares for plan (auth required)
  fastify.get<{ Params: { planId: string } }>(
    '/plans/:planId/share',
    { preHandler: [authMiddleware] },
    async (request, reply) => {
      const { planId } = request.params
      const shares = await controller.listShares(request, reply, planId)
      if (shares) return reply.send(shares)
    }
  )

  // Access share (public, optional password)
  fastify.post<{ Params: { token: string }; Body: { password?: string } }>(
    '/access/:token',
    async (request, reply) => {
      const { token } = request.params
      const share = await controller.accessShare(request, reply, token, request.body as any)
      if (share) return reply.send(share)
    }
  )

  // Public plan view (public, optional password)
  fastify.post<{ Params: { token: string }; Body: { password?: string } }>(
    '/public/:token',
    async (request, reply) => {
      const { token } = request.params
      const result = await controller.getPublicPlan(request, reply, token, request.body ?? {})
      if (result) return reply.send(result)
    }
  )

  // Join a shared plan (auth required)
  // This adds the user as a collaborator on the ORIGINAL plan (not a copy)
  fastify.post<{ Params: { token: string }; Body: { password?: string } }>(
    '/join/:token',
    { preHandler: [authMiddleware] },
    async (request, reply) => {
      const { token } = request.params
      const result = await controller.joinPlan(request, reply, token, request.body ?? {})
      if (result) return reply.send(result)
    }
  )

  // Delete share (auth required)
  fastify.delete<{ Params: { shareId: string } }>(
    '/:shareId',
    { preHandler: [authMiddleware] },
    async (request, reply) => {
      const { shareId } = request.params
      await controller.deleteShare(request, reply, shareId)
      return reply.code(204).send()
    }
  )

  // Get comments (public)
  fastify.get<{ Params: { planId: string } }>(
    '/plans/:planId/comments',
    async (request, reply) => {
      const { planId } = request.params
      const comments = await controller.getComments(request, reply, planId)
      if (comments) return reply.send(comments)
    }
  )

  // Create comment (auth required)
  fastify.post<{ Params: { planId: string }; Body: { content: string; parentId?: string } }>(
    '/plans/:planId/comments',
    { preHandler: [authMiddleware] },
    async (request, reply) => {
      const { planId } = request.params
      const comment = await controller.createComment(request, reply, planId, request.body as any)
      if (comment) return reply.send(comment)
    }
  )

  // Delete comment (auth required)
  fastify.delete<{ Params: { commentId: string } }>(
    '/comments/:commentId',
    { preHandler: [authMiddleware] },
    async (request, reply) => {
      const { commentId } = request.params
      await controller.deleteComment(request, reply, commentId)
      return reply.code(204).send()
    }
  )

  // ===== COLLABORATOR MANAGEMENT =====

  // Get collaborators for a plan
  fastify.get<{ Params: { planId: string } }>(
    '/plans/:planId/collaborators',
    { preHandler: [authMiddleware] },
    async (request, reply) => {
      return controller.getCollaborators(request, reply)
    }
  )

  // Update collaborator role
  fastify.patch<{
    Params: { planId: string; collaboratorId: string }
    Body: { role: 'admin' | 'editor' | 'viewer' }
  }>(
    '/plans/:planId/collaborators/:collaboratorId/role',
    { preHandler: [authMiddleware] },
    async (request, reply) => {
      return controller.updateCollaboratorRole(request, reply)
    }
  )

  // Update collaborator permissions
  fastify.patch<{
    Params: { planId: string; collaboratorId: string }
    Body: { permissions: Record<string, boolean> }
  }>(
    '/plans/:planId/collaborators/:collaboratorId/permissions',
    { preHandler: [authMiddleware] },
    async (request, reply) => {
      return controller.updateCollaboratorPermissions(request, reply)
    }
  )

  // Remove collaborator
  fastify.delete<{ Params: { planId: string; collaboratorId: string } }>(
    '/plans/:planId/collaborators/:collaboratorId',
    { preHandler: [authMiddleware] },
    async (request, reply) => {
      return controller.removeCollaborator(request, reply)
    }
  )

  // Leave plan (self-remove)
  fastify.post<{ Params: { planId: string } }>(
    '/plans/:planId/leave',
    { preHandler: [authMiddleware] },
    async (request, reply) => {
      return controller.leavePlan(request, reply)
    }
  )
}
